import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import type { FunctionInvokeOptions } from "@supabase/supabase-js";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * Invokes a Supabase Edge Function with automatic retry on network errors.
 * Particularly useful for mobile devices with unstable connections.
 */
export async function invokeWithRetry<T = unknown>(
  functionName: string,
  options?: FunctionInvokeOptions,
  retries = MAX_RETRIES,
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, options);

    if (error) {
      // Check if it's a network error that should be retried
      const isNetworkError =
        error.message?.includes("Failed to send") ||
        error.message?.includes("NetworkError") ||
        error.message?.includes("fetch");

      if (isNetworkError && retries > 0) {
        logger.info(`Retrying ${functionName} (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})...`);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return invokeWithRetry<T>(functionName, options, retries - 1);
      }

      return { data: null, error: new Error(error.message) };
    }

    // Dual-Error-Check: validate application-level errors in data
    // (transport error is null but edge function returned an error payload)
    if (
      !data ||
      (typeof data === "object" &&
        data !== null &&
        "error" in data &&
        (data as Record<string, unknown>).error)
    ) {
      const appError = (data as Record<string, unknown>)?.error;
      const message = typeof appError === "string" ? appError : "Unknown edge function error";
      return { data: null, error: new Error(message) };
    }

    return { data: data as T, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error("Unknown error");

    // Retry on network-related errors
    const isNetworkError =
      error.message?.includes("Failed to send") ||
      error.message?.includes("NetworkError") ||
      error.message?.includes("fetch");

    if (isNetworkError && retries > 0) {
      logger.info(`Retrying ${functionName} (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})...`);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return invokeWithRetry<T>(functionName, options, retries - 1);
    }

    return { data: null, error };
  }
}
